import React from 'react';
import { Table } from 'antd';
import type { ColumnsType, TablePaginationConfig, TableProps } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import numeral from 'numeral';
import dayjs from 'dayjs';
import { type UsersyncStatisticsFilters, type UsersyncStatisticsTableRow } from '../../../../types/usersyncStatistics';
import { TruncatedCell } from '../../finances-v2/components/TruncatedCell';
import { UsersyncStatisticsSummary } from './UsersyncStatisticsSummary';

type OnTableChange = TableProps<UsersyncStatisticsTableRow>['onChange'];

interface UsersyncStatisticsTableProps {
  data: UsersyncStatisticsTableRow[];
  loading: boolean;
  filters: UsersyncStatisticsFilters;
  pagination: false | TablePaginationConfig;
  onTableChange?: OnTableChange;
  sortState: {
    by?: string;
    direction?: 'ASC' | 'DESC';
  };
}

export const UsersyncStatisticsTable: React.FC<UsersyncStatisticsTableProps> = ({
  data,
  loading,
  filters,
  pagination,
  onTableChange,
  sortState,
}) => {
  const toAntSortOrder = (direction?: 'ASC' | 'DESC'): SortOrder | undefined => {
    if (!direction) return undefined;
    return direction === 'ASC' ? 'ascend' : 'descend';
  };

  const getSortOrderForColumn = (columnKey: string): SortOrder | undefined => {
    if (sortState.by !== columnKey) return undefined;
    return toAntSortOrder(sortState.direction);
  };

  const dynamicColumns = filters.groupBy ?? [];

  const columns: ColumnsType<UsersyncStatisticsTableRow> = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      align: 'right',
    },
  ];

  if (filters.format === 'by_day' || filters.format === 'by_hour') {
    columns.push({
      title: 'Date',
      dataIndex: 'datetime',
      key: 'datetime',
      render: (datetime: string) =>
        filters.format === 'by_hour'
          ? dayjs(datetime).format('YYYY-MM-DD HH:mm')
          : dayjs(datetime).format('YYYY-MM-DD'),
      sorter: true,
      sortOrder: getSortOrderForColumn('datetime'),
      sortDirections: ['ascend', 'descend'],
    });
  }

  dynamicColumns.forEach((field) => {
    columns.push({
      title:
        field === 'sspId'
          ? 'SSP'
          : field === 'dspId'
            ? 'DSP'
            : field === 'tagType'
              ? 'Tag Type'
              : field.charAt(0).toUpperCase() + field.slice(1),
      dataIndex: field,
      key: field,
      render: (value: string | number) => <TruncatedCell text={String(value || '-')} />,
      sorter: true,
      sortOrder: getSortOrderForColumn(field),
      sortDirections: ['ascend', 'descend'],
    });
  });

  const percentRenderer = (value?: number) =>
    value === undefined || value === null ? '-' : `${numeral(value).format('0.00')}%`;

  columns.push(
    {
      title: 'Initial Requests',
      dataIndex: 'initialRequest',
      key: 'initialRequest',
      align: 'right',
      render: (value: number) => numeral(value || 0).format('0,0'),
      sorter: true,
      sortOrder: getSortOrderForColumn('initialRequest'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Eligible',
      dataIndex: 'eligibleForSync',
      key: 'eligibleForSync',
      align: 'right',
      render: (value: number) => numeral(value || 0).format('0,0'),
      sorter: true,
      sortOrder: getSortOrderForColumn('eligibleForSync'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Generated',
      dataIndex: 'syncAttemptGenerated',
      key: 'syncAttemptGenerated',
      align: 'right',
      render: (value: number) => numeral(value || 0).format('0,0'),
      sorter: true,
      sortOrder: getSortOrderForColumn('syncAttemptGenerated'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Callbacks',
      dataIndex: 'callbackReceived',
      key: 'callbackReceived',
      align: 'right',
      render: (value: number) => numeral(value || 0).format('0,0'),
      sorter: true,
      sortOrder: getSortOrderForColumn('callbackReceived'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Stored',
      dataIndex: 'mappingStored',
      key: 'mappingStored',
      align: 'right',
      render: (value: number) => numeral(value || 0).format('0,0'),
      sorter: true,
      sortOrder: getSortOrderForColumn('mappingStored'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Drops',
      dataIndex: 'dropCount',
      key: 'dropCount',
      align: 'right',
      render: (value: number) => numeral(value || 0).format('0,0'),
      sorter: true,
      sortOrder: getSortOrderForColumn('dropCount'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Callback Rate',
      dataIndex: 'callbackRate',
      key: 'callbackRate',
      align: 'right',
      render: percentRenderer,
      sorter: true,
      sortOrder: getSortOrderForColumn('callbackRate'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Store Rate',
      dataIndex: 'storeRate',
      key: 'storeRate',
      align: 'right',
      render: percentRenderer,
      sorter: true,
      sortOrder: getSortOrderForColumn('storeRate'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'End-to-End',
      dataIndex: 'endToEndRate',
      key: 'endToEndRate',
      align: 'right',
      render: percentRenderer,
      sorter: true,
      sortOrder: getSortOrderForColumn('endToEndRate'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Drop Rate',
      dataIndex: 'dropRate',
      key: 'dropRate',
      align: 'right',
      render: percentRenderer,
      sorter: true,
      sortOrder: getSortOrderForColumn('dropRate'),
      sortDirections: ['ascend', 'descend'],
    }
  );

  return (
    <Table<UsersyncStatisticsTableRow>
      size="small"
      bordered
      rowKey="key"
      loading={loading}
      dataSource={data}
      columns={columns}
      pagination={pagination}
      onChange={onTableChange}
      scroll={{ x: 'max-content' }}
      summary={() => <UsersyncStatisticsSummary data={data} filters={filters} />}
    />
  );
};
